/**
 * Converts markdown to HTML for rich text formatting
 * Handles common markdown patterns similar to Slack's behavior
 */
function markdownToHtml(markdown: string): string {
  if (!markdown) return ''

  let html = markdown

  // Escape HTML entities first to prevent injection
  const escapeHtml = (text: string) => {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Code blocks (triple backticks) - preserve as <pre><code>
  html = html.replace(/```([\s\S]*?)```/g, (match, code) => {
    return `<pre><code>${escapeHtml(code.trim())}</code></pre>`
  })

  // Inline code (single backticks)
  html = html.replace(/`([^`]+)`/g, (match, code) => {
    return `<code>${escapeHtml(code)}</code>`
  })

  // Bold (**text** or __text__)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>')

  // Italic (*text* or _text_)
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>')
  html = html.replace(/_(.*?)_/g, '<em>$1</em>')

  // Strikethrough (~~text~~)
  html = html.replace(/~~(.*?)~~/g, '<del>$1</del>')

  // Links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

  // Headers (# Header, ## Header, etc.) - process in order from most specific to least
  html = html.replace(/^#### (.*$)/gm, '<h4>$1</h4>')
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>')
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>')
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>')

  // Blockquotes (> text) - process before lists
  html = html.replace(/^>\s+(.*)$/gm, '<blockquote>$1</blockquote>')

  // Process lists - group consecutive list items
  const lines = html.split('\n')
  const processedLines: string[] = []
  let inUnorderedList = false
  let inOrderedList = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const unorderedMatch = line.match(/^[*\-+]\s+(.*)$/)
    const orderedMatch = line.match(/^\d+\.\s+(.*)$/)

    if (unorderedMatch) {
      if (!inUnorderedList) {
        if (inOrderedList) {
          processedLines.push('</ol>')
          inOrderedList = false
        }
        processedLines.push('<ul>')
        inUnorderedList = true
      }
      processedLines.push(`<li>${unorderedMatch[1]}</li>`)
    } else if (orderedMatch) {
      if (!inOrderedList) {
        if (inUnorderedList) {
          processedLines.push('</ul>')
          inUnorderedList = false
        }
        processedLines.push('<ol>')
        inOrderedList = true
      }
      processedLines.push(`<li>${orderedMatch[1]}</li>`)
    } else {
      if (inUnorderedList) {
        processedLines.push('</ul>')
        inUnorderedList = false
      }
      if (inOrderedList) {
        processedLines.push('</ol>')
        inOrderedList = false
      }
      processedLines.push(line)
    }
  }

  // Close any open lists
  if (inUnorderedList) {
    processedLines.push('</ul>')
  }
  if (inOrderedList) {
    processedLines.push('</ol>')
  }

  html = processedLines.join('\n')

  // Line breaks (double newline = paragraph, single newline = br)
  // But preserve block-level elements
  html = html.replace(/\n\n+/g, '\n\n')
  html = html
    .split('\n')
    .map((line) => {
      // Don't add <br> to empty lines or block-level elements
      if (!line.trim() || /^<(h[1-6]|p|ul|ol|li|blockquote|pre)/.test(line.trim())) {
        return line
      }
      return line || '<br/>'
    })
    .join('\n')

  // Wrap in paragraph if not already wrapped
  if (!html.trim().startsWith('<')) {
    html = `<p>${html}</p>`
  }

  // Clean up empty paragraphs and fix nesting
  html = html.replace(/<p>\s*<\/p>/g, '')
  html = html.replace(/<p>(<[^>]+>)/g, '$1')
  html = html.replace(/(<\/[^>]+>)<\/p>/g, '$1')

  return html
}

/**
 * Extracts plain text from markdown/HTML content
 * Strips all formatting while preserving readable text
 */
function extractPlainText(content: string): string {
  if (!content) return ''

  let text = content

  // Remove code blocks (keep content)
  text = text.replace(/```[\s\S]*?```/g, (match) => {
    return match.replace(/```/g, '').trim()
  })

  // Remove inline code markers
  text = text.replace(/`([^`]+)`/g, '$1')

  // Remove markdown formatting
  text = text.replace(/\*\*(.*?)\*\*/g, '$1') // Bold
  text = text.replace(/__(.*?)__/g, '$1') // Bold
  text = text.replace(/\*(.*?)\*/g, '$1') // Italic
  text = text.replace(/_(.*?)_/g, '$1') // Italic
  text = text.replace(/~~(.*?)~~/g, '$1') // Strikethrough
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links, keep text
  text = text.replace(/#{1,6}\s/g, '') // Headers
  text = text.replace(/^[\\*\-\\+]\s+/gm, '') // List markers
  text = text.replace(/^\d+\.\s+/gm, '') // Ordered list markers
  text = text.replace(/^>\s+/gm, '') // Blockquotes

  // Remove HTML tags if any
  const div = document.createElement('div')
  div.innerHTML = text
  text = div.textContent || div.innerText || ''

  // Normalize whitespace
  text = text.replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines
  text = text.replace(/[ \t]+/g, ' ') // Multiple spaces to single
  text = text.trim()

  return text
}

/**
 * Enhanced clipboard copy function that writes both plain text and HTML formats
 * Similar to Slack's behavior:
 * - When pasting in the same app: preserves formatting (rich text)
 * - When pasting in external apps: uses plain text only
 */
export default async function copyToClipboard(content: string): Promise<void> {
  if (!content) return

  const plainText = extractPlainText(content)
  const htmlText = markdownToHtml(content)

  try {
    // Use modern Clipboard API with multiple formats if available
    if (navigator.clipboard && window.isSecureContext && navigator.clipboard.write) {
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([htmlText], { type: 'text/html' }),
      })
      await navigator.clipboard.write([clipboardItem])
      return
    }

    // Fallback for older browsers or non-HTTPS contexts
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(plainText)
      return
    }

    // Final fallback using execCommand
    const textArea = document.createElement('textarea')
    textArea.value = plainText
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    document.execCommand('copy')
    textArea.remove()
  } catch (error) {
    console.error('Failed to copy to clipboard:', error)
    // Last resort: try plain text only
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(plainText)
      }
    } catch (fallbackError) {
      console.error('Fallback copy also failed:', fallbackError)
    }
  }
}
